import React, { useState } from "react";
import { 
  Box, 
  Button, 
  Grid, 
  Paper,
  Table, 
  TableBody, 
  TableCell, 
  TableContainer, 
  TableHead, 
  TableRow,
  Typography,
  CircularProgress,
  Stack
} from "@mui/material";
import { DatePicker } from "@mui/x-date-pickers/DatePicker";
import dayjs from "dayjs";
import { api } from "../../helpers/apiRequest";
import { toast } from "react-toastify";
import DescriptionIcon from "@mui/icons-material/Description";
import PictureAsPdfIcon from '@mui/icons-material/PictureAsPdf';
import FileDownloadIcon from '@mui/icons-material/FileDownload';
import EmailIcon from '@mui/icons-material/Email';
import colors from "../../theme/Colors";
import { exportToPDF, exportToExcel, getPDFBlob } from "./ReportUtils";
import EmailReportDialog from "../EmailReportDialog";

const PurchaseOrderBalanceReport = ({ customers }) => {
  const [loadingReport, setLoadingReport] = useState(false);
  const [emailDialogOpen, setEmailDialogOpen] = useState(false);
  const [selectedEmailCustomer, setSelectedEmailCustomer] = useState('');
  const [sendingEmail, setSendingEmail] = useState(false);
  const [reportData, setReportData] = useState([]);
  const [showReport, setShowReport] = useState(false);
  const [filters, setFilters] = useState({
    dateFrom: null,
    dateTo: null
  });

  const generateReport = async (exportType = 'html') => {
    const params = new URLSearchParams();
    
    if (filters.dateFrom) {
      params.append('date_from', filters.dateFrom.format("YYYY-MM-DD"));
    }
    
    if (filters.dateTo) {
      params.append('date_to', filters.dateTo.format("YYYY-MM-DD"));
    }
    
    const queryString = params.toString();
    
    setLoadingReport(true);
    setShowReport(false);

    try {
      const response = await api.get(
        `/reports/purchase-order-report${queryString ? `?${queryString}` : ''}`
      );
      console.log(response.data)
      if (response.data) {
        setReportData(response.data.data.map(item => ({
          poNumber: item.poNumber,
          exFactoryDate: item.exFactoryDate,
          poQuantity: item.poQuantity,
          balanceQuantity: item.balanceQuantity,
        })) || []);
        setShowReport(true);
        
        const dateFromStr = filters.dateFrom ? filters.dateFrom.format("YYYY-MM-DD") : "All";
        const dateToStr = filters.dateTo ? filters.dateTo.format("YYYY-MM-DD") : "All";
        
        if (exportType === 'excel') {
          const excelData = response.data.data.map(row => ({
            "Po #": row.poNumber,
            "Ex Factory Date": dayjs(row.exFactoryDate).format("DD/MM/YYYY"),
            "Po Quantity": row.poQuantity,
            "Balance Quantity": row.balanceQuantity,
      
          }));
          
          exportToExcel(excelData, `Purchase_Order_Balance_Report_${dayjs().format("YYYY-MM-DD_HH-mm-ss")}`);
          toast.success("Excel report generated successfully");
          return;
        }
        
        if (exportType === 'pdf') {
          let htmlContent = `
            <!DOCTYPE html>
            <html>
            <head>
              <title>Purchase Order Balance Report</title>
              <style>
                  body { font-family: Arial, sans-serif; margin: 15px; padding: 50px 30px; align-items:center;-webkit-user-select: text;-moz-user-select: text;-ms-user-select: text; user-select: text;  }
             h1 { color: #1abc9c; text-align: center; }
              table { border-collapse: collapse; width: 95%; margin: 15px; }
                th { background-color: #1abc9c; color: white; text-align: left; padding: 8px; }
                td { border: 1px solid #ddd; padding: 8px; }
                tr:nth-child(even) { background-color: #f2f2f2; }
                .report-header { margin-bottom: 20px; }
                .report-filters { margin: 15px; font-size: 14px; }
               
              </style>
            </head>
            <body>
              <div class="report-header">
                <h1>Purchase Order Balance Report</h1>
                <div class="report-filters">
                  <p><strong>Date Range:</strong> ${dateFromStr} to ${dateToStr}</p>
                  <p>Generated on: ${dayjs().format("YYYY-MM-DD HH:mm")}</p>  
                </div>
                
              </div>
              
              <table>
                <thead>
                  <tr>
                    <th>Po #</th>
                    <th>Ex Factory Date</th>
                    <th>Po Quantity</th>
                    <th>Balance Quantity</th>
                  </tr>
                </thead>
                <tbody>
                  ${response.data.data.map(row => `
                    <tr>
                      <td>${row.poNumber}</td>
                      <td>${dayjs(row.exFactoryDate).format("DD/MM/YYYY")}</td>
                      <td>${row.poQuantity}</td>
                      <td>${row.balanceQuantity}</td>
                 
                    </tr>
                  `).join('')}
                </tbody>
              </table>
            </body>
            </html>
          `;
          
          exportToPDF(htmlContent, `Purchase_Order_Balance_Report_${dayjs().format("YYYY-MM-DD_HH-mm-ss")}`);
          toast.success("PDF report generated successfully");
          return;
        }
        
        if (exportType === 'email') {
          setEmailDialogOpen(true);
          setLoadingReport(false);
          return;
        }
        
        if (exportType === 'html') {
          const reportWindow = window.open('', '_blank');
          if (!reportWindow) {
            toast.error("Please allow popups for this site to view reports");
            return;
          }
          
          let htmlContent = `
            <!DOCTYPE html>
            <html>
            <head>
              <title>Purchase Order Balance Report</title>
              <style>
                body { font-family: Arial, sans-serif; margin: 20px; }
                h1 { color: #1abc9c; }
                table { border-collapse: collapse; width: 100%; margin-top: 20px; }
                th { background-color: #1abc9c; color: white; text-align: left; padding: 8px; }
                td { border: 1px solid #ddd; padding: 8px; }
                tr:nth-child(even) { background-color: #f2f2f2; }
                .report-header { margin-bottom: 20px; }
                .report-filters { margin-bottom: 15px; font-size: 14px; }
              </style>
            </head>
            <body>
              <div class="report-header">
                <h1>Purchase Order Balance Report</h1>
                <div class="report-filters">
                  <p><strong>Date Range:</strong> ${dateFromStr} to ${dateToStr}</p>
                </div>
                <p>Generated on: ${dayjs().format("YYYY-MM-DD HH:mm")}</p>
              </div>
              
              <table>
                <thead>
                  <tr>
                    <th>Po #</th>
                    <th>Ex Factory Date</th>
                    <th>Po Quantity</th>
                    <th>Balance Quantity</th>
                  </tr>
                </thead>
                <tbody>
                  ${response.data.data.map(row => `
                    <tr>
                        <td>${row.poNumber}</td>
                      <td>${dayjs(row.exFactoryDate).format("DD/MM/YYYY")}</td>
                      <td>${row.poQuantity}</td>
                      <td>${row.balanceQuantity}</td>
                 
                    </tr>
                  `).join('')}
                </tbody>
              </table>
            </body>
            </html>
          `;
          
          reportWindow.document.write(htmlContent);
          reportWindow.document.close();
          toast.success("Report generated successfully");
        }
      } else {
        toast.error("No data available for the selected filters");
      }
    } catch (error) {
      console.error("Error fetching purchase order balance report:", error);
      toast.error("Failed to generate report");
    } finally {
      setLoadingReport(false);
    }
  };

  const sendReportByEmail = async () => {
    if (!selectedEmailCustomer) {
      toast.error('Please select a customer');
      return;
    }

    setSendingEmail(true);
    try {
      // Generate the report HTML
      const params = new URLSearchParams();
      if (filters.dateFrom) params.append('date_from', filters.dateFrom.format("YYYY-MM-DD"));
      if (filters.dateTo) params.append('date_to', filters.dateTo.format("YYYY-MM-DD"));
      
      const queryString = params.toString();
      
      const response = await api.get(`/reports/purchase-order-report${queryString ? `?${queryString}` : ''}`);
      
      if (response.data) {
        const dateFromStr = filters.dateFrom ? filters.dateFrom.format("YYYY-MM-DD") : "All";
        const dateToStr = filters.dateTo ? filters.dateTo.format("YYYY-MM-DD") : "All";
        
        let htmlContent = `
          <!DOCTYPE html>
          <html>
          <head>
            <title>Purchase Order Balance Report</title>
            <style>
              body { font-family: Arial, sans-serif; margin: 20px; }
              h1 { color: #1abc9c; }
              table { border-collapse: collapse; width: 100%; margin-top: 20px; }
              th { background-color: #1abc9c; color: white; text-align: left; padding: 8px; }
              td { border: 1px solid #ddd; padding: 8px; }
              tr:nth-child(even) { background-color: #f2f2f2; }
              .report-header { margin-bottom: 20px; }
              .report-filters { margin-bottom: 15px; font-size: 14px; }
            </style>
          </head>
          <body>
            <div class="report-header">
              <h1>Purchase Order Balance Report</h1>
              <div class="report-filters">
                <p><strong>Date Range:</strong> ${dateFromStr} to ${dateToStr}</p>
              </div>
              <p>Generated on: ${dayjs().format("YYYY-MM-DD HH:mm")}</p>
            </div>
            
            <table>
              <thead>
                <tr>
                  <th>Po #</th>
                  <th>Ex Factory Date</th>
                  <th>Po Quantity</th>
                  <th>Balance Quantity</th>
                </tr>
              </thead>
              <tbody>
                ${response.data.data.map(row => `
                  <tr>
                    <td>${row.poNumber}</td>
                    <td>${dayjs(row.exFactoryDate).format("DD/MM/YYYY")}</td>
                    <td>${row.poQuantity}</td>
                    <td>${row.balanceQuantity}</td>
                  </tr>
                `).join('')}
              </tbody>
            </table>
          </body>
          </html>
        `;
        
        // Convert HTML to PDF blob
        const pdfBlob = await getPDFBlob(htmlContent);
        
        // Create FormData and append the PDF blob
        const formData = new FormData();
        formData.append('image', pdfBlob, `Purchase_Order_Balance_Report_${dayjs().format("YYYY-MM-DD_HH-mm-ss")}.pdf`);
        formData.append('userId', selectedEmailCustomer);
        formData.append('type', "Report");
        // Send the email
        const emailResponse = await api.post('inventory/upload', formData, {
          headers: {
            'Content-Type': 'multipart/form-data'
          }
        });
        
        if (emailResponse.status === 200) {
          toast.success('Report sent by email successfully');
          setEmailDialogOpen(false);
          setSelectedEmailCustomer('');
        } else {
          toast.error('Failed to send report by email');
        }
      } else {
        toast.error('No data available for the selected filters');
      }
    } catch (error) {
      console.error('Error sending report by email:', error);
      toast.error('Failed to send report by email');
    } finally {
      setSendingEmail(false);
    }
  };

  return (
    <Grid container spacing={3}>
      <Grid item xs={12}>
        <Grid container spacing={3} alignItems="center">
          <Grid item xs={12} sm={5}>
            <DatePicker
              label="From Date"
              value={filters.dateFrom}
              onChange={(newValue) => setFilters({ ...filters, dateFrom: newValue })}
              slotProps={{ textField: { fullWidth: true } }}
            />
          </Grid>
          <Grid item xs={12} sm={5}>
            <DatePicker
              label="To Date"
              value={filters.dateTo}
              onChange={(newValue) => setFilters({ ...filters, dateTo: newValue })}
              slotProps={{ textField: { fullWidth: true } }}
            />
          </Grid>
          <Grid item xs={12} sm={2}>
            <Stack spacing={1}>
              <Button
                variant="contained"
                fullWidth
                onClick={() => generateReport('html')}
                disabled={loadingReport || sendingEmail}
                startIcon={<DescriptionIcon />}
                sx={{ 
                  height: "38px", 
                  backgroundColor: colors.primary, 
                  '&:hover': { 
                    backgroundColor: colors.primary, 
                    opacity: 0.9 
                  } 
                }}
              >
                {loadingReport ? <CircularProgress size={24} color="inherit" /> : "View"}
              </Button>
              <Button
                variant="outlined"
                fullWidth
                onClick={() => generateReport('pdf')}
                disabled={loadingReport || sendingEmail}
                startIcon={<PictureAsPdfIcon />}
                sx={{ 
                  height: "38px",
                  borderColor: colors.primary,
                  color: colors.primary,
                  '&:hover': { 
                    borderColor: colors.primary,
                    opacity: 0.9 
                  } 
                }}
              >
                PDF
              </Button>
              <Button
                variant="outlined"
                fullWidth
                onClick={() => generateReport('excel')}
                disabled={loadingReport || sendingEmail}
                startIcon={<FileDownloadIcon />}
                sx={{ 
                  height: "38px",
                  borderColor: colors.primary,
                  color: colors.primary,
                  '&:hover': { 
                    borderColor: colors.primary,
                    opacity: 0.9 
                  } 
                }}
              >
                Excel
              </Button>
              <Button
                variant="outlined"
                fullWidth
                onClick={() => generateReport('email')}
                disabled={loadingReport || sendingEmail}
                startIcon={<EmailIcon />}
                sx={{ 
                  height: "38px",
                  borderColor: colors.primary,
                  color: colors.primary,
                  '&:hover': { 
                    borderColor: colors.primary,
                    opacity: 0.9 
                  } 
                }}
              >
                Email
              </Button>
            </Stack>
          </Grid>
        </Grid>
      </Grid>

      <EmailReportDialog
        open={emailDialogOpen}
        handleClose={() => {
          setEmailDialogOpen(false);
          setSelectedEmailCustomer('');
        }}
        selectedCustomer={selectedEmailCustomer}
        setSelectedCustomer={setSelectedEmailCustomer}
        customers={customers}
        isLoading={sendingEmail}
        handleSendEmail={sendReportByEmail}
      />

      {loadingReport && (
        <Grid item xs={12}>
          <Box sx={{ display: "flex", justifyContent: "center", my: 4 }}>
            <CircularProgress />
          </Box>
        </Grid>
      )}

      {showReport && !loadingReport && reportData.length === 0 && (
        <Grid item xs={12}>
          <Typography variant="body1" sx={{ textAlign: "center", my: 4 }}>
            No purchase order balance data found for the selected period.
          </Typography>
        </Grid>
      )}

      {showReport && !loadingReport && reportData.length > 0 && (
        <Grid item xs={12}>
          <TableContainer component={Paper} sx={{ mt: 2, maxHeight: "60vh" }}>
            <Table stickyHeader aria-label="purchase order balance table">
              <TableHead>
                <TableRow sx={{ backgroundColor: colors.lightGray }}>
                  <TableCell sx={{ fontWeight: "bold" }}>PO #</TableCell>
                  <TableCell sx={{ fontWeight: "bold" }}>Ex Factory Date</TableCell>
                  <TableCell sx={{ fontWeight: "bold" }}>Po Quantity</TableCell>
                  <TableCell sx={{ fontWeight: "bold" }}>Balance Quantity</TableCell>
                 
                </TableRow>
              </TableHead>
              <TableBody>
                {reportData.map((row) => (
                  <TableRow key={row.id}>
                    <TableCell>{row.poNumber}</TableCell>
                    <TableCell>{dayjs(row.exFactoryDate).format("DD/MM/YYYY")}</TableCell>
                    <TableCell>{row.poQuantity}</TableCell>
                    <TableCell>{row.balanceQuantity}</TableCell>
                  
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </Grid>
      )}
    </Grid>
  );
};

export default PurchaseOrderBalanceReport;
